/**
 * Links that open the Suite on a given app, tab and job: what the feedback
 * inbox's "Open this page" uses.
 *
 *   https://nycsuite.io/?go=<appId>&tab=<tab label>&job=<jobId>
 *
 * App.jsx reads the three parameters once, before first paint, and takes
 * them out of the address bar (the ?convert= precedent). After sign-in it
 * opens the app (only one this person may open, never for an event account),
 * the tab by its label (only one this person's menu shows), and Jobs or
 * Ingest's rig console hands the job id over through takePendingJob. Anything
 * not recognised is ignored.
 */
const APP_RE = /^[a-z][a-z0-9_-]{0,39}$/;
const JOB_RE = /^(?!__)[A-Za-z0-9_-]{1,128}$/;
const TAB_RE = /^[A-Za-z0-9 &'-]{1,40}$/;

export const SUITE_ORIGIN = "https://nycsuite.io";

export function parseGoParams(search) {
  const p = new URLSearchParams(search || "");
  const app = p.get("go");
  if (!app || !APP_RE.test(app)) return null;
  const tab = p.get("tab");
  const job = p.get("job");
  return {
    app,
    tab: tab && TAB_RE.test(tab) ? tab : null,
    job: job && JOB_RE.test(job) ? job : null,
  };
}

/** The link for a feedback report's page, or null when it names no app. */
export function pageLink(page, origin = SUITE_ORIGIN) {
  if (!page || !page.app || !APP_RE.test(page.app)) return page ? `${origin}/` : null;
  const p = new URLSearchParams();
  p.set("go", page.app);
  if (page.tab && TAB_RE.test(page.tab)) p.set("tab", page.tab);
  const job = page.ids?.jobId;
  if (job && JOB_RE.test(job) && (page.app === "jobs" || page.app === "ingest")) p.set("job", job);
  return `${origin}/?${p.toString()}`;
}

/** Strips the three parameters from the address bar, keeping anything else. */
export function stripGoParams(win = window) {
  const url = new URL(win.location.href);
  let changed = false;
  for (const k of ["go", "tab", "job"]) {
    if (url.searchParams.has(k)) { url.searchParams.delete(k); changed = true; }
  }
  if (changed) win.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
}

let pendingJob = null; // { app, job }

export function setPendingJob(app, job) {
  pendingJob = app && job ? { app, job } : null;
}

/** The job a link asked this app to open, once (null when none). */
export function takePendingJob(app) {
  if (!pendingJob || pendingJob.app !== app) return null;
  const { job } = pendingJob;
  pendingJob = null;
  return job;
}
